import { emit, on } from "../../core/state.js";
import { getTheme, getTenantId } from "../../core/configAccessors.js";

/** When false, promo bg image is used as-is (Wine Engine .promotion parity). */
function shouldDimPromoImage() {
  const v = getTheme("banner_promo_dim", true);
  return v !== false && v !== "false" && v !== 0;
}

function cssUrl(url) {
  return `url("${String(url).replace(/["\\\n\r\f]/g, "\\$&")}")`;
}

function trimStr(v) {
  if (v == null) return null;
  const s = String(v).trim();
  return s.length ? s : null;
}

/** Split main_banner_text into overlay lines (old chatbot-banner-text spans). */
function splitMainBannerLines(text) {
  const raw = String(text || "").trim();
  if (!raw) return [];
  if (/\n/.test(raw)) {
    return raw.split(/\n+/).map((s) => s.trim()).filter(Boolean);
  }
  // e.g. "Patio Views. Signature Wines! ,(SUN-THU)" → two lines like ss2
  if (/,/.test(raw)) {
    return raw.split(/,/).map((s) => s.trim()).filter(Boolean);
  }
  return [raw];
}

function storageKey() {
  return `ws_banner_slot_${getTenantId() || "default"}`;
}

function isRotateEnabled() {
  const nested = getTheme("banner", null) || {};
  const primaryUrl = trimStr(nested.image_url || getTheme("banner_image_url", null));
  const altUrl = trimStr(
    nested.image_url_alt || getTheme("banner_image_url_alt", null),
  );
  const rotateFlag = getTheme("banner_rotate", null);
  const rotateDisabled = rotateFlag === false || rotateFlag === "false";
  return Boolean(altUrl && primaryUrl && !rotateDisabled);
}

/**
 * Hestan-style A/B hero rotation (old createBanner + updatePromotionalBanner).
 *
 * When theme.banner_image_url_alt is set, each Banner render / chat reload
 * shows one slot and flips localStorage so the next open shows the other.
 * Optional _alt promo/CTA keys override the primary copy for slot B; missing
 * alt copy falls back to the primary fields.
 *
 * Disable with banner_rotate: "false" (alt URL ignored for rotation).
 * Tenants without banner_image_url_alt are unchanged (single banner, no
 * localStorage, no reload swap).
 */
function resolveBannerFields() {
  const nested = getTheme("banner", null) || {};

  const primaryUrl = trimStr(nested.image_url || getTheme("banner_image_url", null));
  const altUrl = trimStr(
    nested.image_url_alt || getTheme("banner_image_url_alt", null),
  );

  const shared = {
    curveUrl: trimStr(getTheme("banner_curve_image_url", null)),
    promoBgUrl: trimStr(
      nested.bg_image ||
        getTheme("banner_bg_image", null) ||
        getTheme("baneer_bg_image", null),
    ),
    stripUrl: trimStr(
      nested.strip_image_url || getTheme("banner_strip_image_url", null),
    ),
  };

  const primary = {
    bannerUrl: primaryUrl,
    text: trimStr(nested.text || getTheme("banner_text", null)),
    mainText: trimStr(nested.main_text || getTheme("main_banner_text", null)),
    ctaLabel: trimStr(nested.cta_label || getTheme("banner_cta_label", null)),
    ctaUrl: trimStr(nested.cta_url || getTheme("banner_cta_url", null)),
    ctaQuery: trimStr(nested.cta_query || getTheme("banner_cta_query", null)),
    linkUrl: trimStr(nested.link_url || getTheme("banner_link_url", null)),
  };

  const canRotate = isRotateEnabled();

  let useAlt = false;
  if (canRotate) {
    let slot = 0;
    try {
      slot = localStorage.getItem(storageKey()) === "1" ? 1 : 0;
    } catch {
      /* storage blocked — stay on primary */
    }
    useAlt = slot === 1;
    try {
      localStorage.setItem(storageKey(), useAlt ? "0" : "1");
    } catch {
      /* ignore */
    }
  }

  if (!useAlt) return { ...shared, ...primary };

  // Slot B — prefer _alt keys, fall back to primary promo/CTA.
  return {
    ...shared,
    bannerUrl: altUrl,
    text:
      trimStr(nested.text_alt || getTheme("banner_text_alt", null)) ||
      primary.text,
    mainText:
      trimStr(nested.main_text_alt || getTheme("main_banner_text_alt", null)) ||
      primary.mainText,
    ctaLabel:
      trimStr(nested.cta_label_alt || getTheme("banner_cta_label_alt", null)) ||
      primary.ctaLabel,
    ctaUrl:
      trimStr(nested.cta_url_alt || getTheme("banner_cta_url_alt", null)) ||
      primary.ctaUrl,
    ctaQuery:
      trimStr(nested.cta_query_alt || getTheme("banner_cta_query_alt", null)) ||
      primary.ctaQuery,
    linkUrl:
      trimStr(nested.link_url_alt || getTheme("banner_link_url_alt", null)) ||
      primary.linkUrl,
  };
}

function buildBannerDom(fields) {
  const {
    bannerUrl,
    promoBgUrl,
    stripUrl,
    curveUrl,
    text,
    mainText,
    ctaLabel,
    ctaUrl,
    ctaQuery,
    linkUrl,
  } = fields;

  if (!bannerUrl && !promoBgUrl && !text && !ctaLabel && !mainText) return null;

  const banner = {
    image_url: bannerUrl,
    bg_image: promoBgUrl,
    text,
    main_banner_text: mainText || null,
    cta_label: ctaLabel,
    cta_url: ctaUrl,
    cta_query: ctaQuery,
  };

  const wrap = document.createElement("div");
  wrap.className = "ws-banner";

  const shouldRenderPromo = Boolean(promoBgUrl || text || ctaLabel);
  const useImagePromo = Boolean(promoBgUrl);

  if (bannerUrl) {
    const hero = document.createElement("div");
    hero.className = "ws-banner-hero";

    const img = document.createElement("img");
    img.className = "ws-banner-img";
    img.alt = "";
    img.style.height = "0";
    img.onload = () => {
      img.style.height = "";
    };
    img.onerror = () => {
      hero.remove();
      if (!wrap.querySelector("*")) wrap.remove();
    };
    img.src = bannerUrl;
    if (img.complete && img.naturalHeight) img.style.height = "";
    if (linkUrl) {
      img.style.cursor = "pointer";
      img.addEventListener("click", () => {
        emit("ws-banner-cta-click", { banner });
        window.open(linkUrl, "_blank", "noopener,noreferrer");
      });
    }
    hero.appendChild(img);

    if (mainText) {
      const overlay = document.createElement("div");
      overlay.className = "ws-banner-main-text";
      splitMainBannerLines(mainText).forEach((line) => {
        const span = document.createElement("span");
        span.textContent = line;
        overlay.appendChild(span);
      });
      hero.appendChild(overlay);
    }

    wrap.appendChild(hero);
  }

  if (shouldRenderPromo) {
    const promo = document.createElement("div");
    promo.className = "ws-banner-promo";
    if (useImagePromo) {
      promo.classList.add("ws-banner-promo--image");
      // Inline size/repeat so base .ws-banner-promo (14px tiled dots + cover)
      // cannot shrink/tile a single banner_bg_image layer. Dim overlay is
      // optional (banner_promo_dim); Hestan/Goose/Wine Engine keep it off.
      promo.style.backgroundColor = "transparent";
      promo.style.backgroundSize = shouldDimPromoImage() ? "cover, cover" : "cover";
      promo.style.backgroundPosition = "center";
      promo.style.backgroundRepeat = "no-repeat";
      promo.style.backgroundImage = shouldDimPromoImage()
        ? `${cssUrl(promoBgUrl)}`
        : cssUrl(promoBgUrl);
    } else if (stripUrl) {
      promo.style.backgroundImage = cssUrl(stripUrl);
    }

    if (text) {
      const t = document.createElement("p");
      t.className = "ws-banner-text";
      if (String(text).includes("\n")) {
        t.innerHTML = String(text)
          .split("\n")
          .map((line) => {
            const span = document.createElement("span");
            span.textContent = line;
            return span.outerHTML;
          })
          .join("<br>");
      } else {
        t.textContent = text;
      }
      promo.appendChild(t);
    }

    if (ctaLabel) {
      const cta = document.createElement("button");
      cta.type = "button";
      cta.className = "ws-banner-cta";
      if (String(ctaLabel).includes("\n")) {
        cta.innerHTML = String(ctaLabel)
          .split("\n")
          .map((line) => {
            const span = document.createElement("span");
            span.textContent = line;
            return span.outerHTML;
          })
          .join("<br>");
      } else {
        cta.textContent = ctaLabel;
      }
      cta.addEventListener("click", () => {
        emit("ws-banner-cta-click", { banner });
        if (ctaQuery) {
          emit("ws-chat-request", { text: ctaQuery, source: "prompt" });
        } else if (ctaUrl) {
          window.open(ctaUrl, "_blank", "noopener,noreferrer");
        }
      });
      promo.appendChild(cta);
    }

    if (useImagePromo && linkUrl) {
      promo.style.cursor = "pointer";
      promo.addEventListener("click", (event) => {
        if (event.target.closest(".ws-banner-cta")) return;
        emit("ws-banner-cta-click", { banner });
        window.open(linkUrl, "_blank", "noopener,noreferrer");
      });
    }

    if (curveUrl) {
      const curve = document.createElement("div");
      curve.className = "ws-banner-curve";
      curve.setAttribute("aria-hidden", "true");
      curve.style.backgroundImage = cssUrl(curveUrl);
      promo.appendChild(curve);
    }

    wrap.appendChild(promo);
  }

  return wrap;
}

// Live banner node — swapped on header refresh ONLY when A/B rotation is
// enabled. Other wineries keep the original banner DOM on soft reload (same
// as before this feature).
let liveBannerEl = null;
let reloadBound = false;

function bindReloadRotation() {
  if (reloadBound) return;
  reloadBound = true;
  on("ws-chat-reload", () => {
    if (!isRotateEnabled()) return;
    if (!liveBannerEl?.isConnected) return;
    const next = buildBannerDom(resolveBannerFields());
    if (!next) return;
    liveBannerEl.replaceWith(next);
    liveBannerEl = next;
  });
}

/**
 * Banner — vineyard hero + promotion strip (Thomas Fogarty parity).
 *
 * theme keys (also accepted nested under `theme.banner`):
 *   banner_image_url / banner_image_url_alt — hero A/B (rotate when alt set)
 *   banner_text / banner_text_alt           — promo caption
 *   banner_cta_label / banner_cta_label_alt
 *   banner_cta_query / banner_cta_query_alt
 *   banner_cta_url / banner_cta_url_alt
 *   banner_rotate                           — "false" disables A/B even if alt set
 *   main_banner_text, banner_bg_image, banner_link_url, …
 */
export default class Banner {
  render() {
    const fields = resolveBannerFields();
    const wrap = buildBannerDom(fields);
    if (!wrap) return null;
    liveBannerEl = wrap;
    bindReloadRotation();
    return wrap;
  }
}
